"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { LoaderIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { getUserFromDB } from '@/lib/user';
import posthog from "posthog-js";
function CheckoutContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const didCheckRef = useRef(false);
  const priceId = searchParams.get('priceId')?.trim() ?? '';
  const checkoutPath = useMemo(() => {
    if (!priceId) return '/checkout';
    return `/checkout?priceId=${encodeURIComponent(priceId)}`;
  }, [priceId]);
  useEffect(() => {
    posthog.capture("checkout_viewed", {
      price_id: priceId || null
    });
    if (!priceId) {
      setError('Missing price. Please choose a plan.');
      posthog.capture("checkout_missing_price");
      setIsLoading(false);
      return;
    }
    const run = async () => {
      if (didCheckRef.current) return;
      didCheckRef.current = true;
      const {
        data
      } = await supabase.auth.getUser();
      if (!data.user) {
        posthog.capture("checkout_redirect_auth", {
          reason: "no_user"
        });
        router.replace(`/auth?next=${encodeURIComponent(checkoutPath)}`);
        return;
      }
      try {
        const {
          data: sessionData
        } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token ?? '';
        if (!accessToken) {
          posthog.capture("checkout_redirect_auth", {
            reason: "no_access_token"
          });
          router.replace(`/auth?next=${encodeURIComponent(checkoutPath)}`);
          return;
        }
        const userData = await getUserFromDB();
        const hasActiveSubscription = userData?.active_subscription?.status === 'active' || userData?.active_subscription?.status === 'trialing';
        if (hasActiveSubscription) {
          toast.success('You already have an active subscription. Manage or upgrade it in Settings.');
          posthog.capture("checkout_already_subscribed", {
            plan_code: userData?.active_subscription?.plan_code ?? null
          });
          router.replace('/settings');
          return;
        }
        const response = await fetch('/api/stripe/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            priceId
          })
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          setError(result.error ?? 'Unable to start checkout.');
          posthog.capture("checkout_start_failed", {
            status: response.status,
            error: result.error ?? null
          });
          posthog.captureException(new Error("Stripe checkout failed"), {
            status: response.status,
            error: result.error,
            price_id: priceId
          });
          setIsLoading(false);
          return;
        }
        const result_0 = await response.json();
        if (result_0?.url) {
          posthog.capture("checkout_redirected", {
            price_id: priceId
          });
          window.location.href = result_0.url;
          return;
        }
        setError('No checkout URL returned.');
        posthog.capture("checkout_start_failed", {
          status: response.status,
          error: "missing_url"
        });
        posthog.captureException(new Error("Stripe checkout missing URL"), {
          status: response.status,
          price_id: priceId
        });
      } catch {
        setError('Unable to start checkout.');
        posthog.capture("checkout_start_failed", {
          error: "exception"
        });
        posthog.captureException(new Error("Checkout request failed"));
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [checkoutPath, priceId, router, supabase]);
  return <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-semibold text-ink">Starting checkout</h1>
        <p className="text-muted-ink flex justify-center items-center gap-2">
          {isLoading ? <>
              <LoaderIcon className="animate-spin" />
              Redirecting you to Stripe...
            </> : error || 'Please try again.'}
        </p>
        {error && <div className="rounded-lg border border-warn-line bg-warn-bg p-4 text-left text-sm text-warn-ink">
            <p className="font-medium">Troubleshooting</p>
            <p className="mt-2 text-warn-ink/80">
              Check the Stripe event payload for <code className="font-mono">data.object.metadata</code> and, for
              invoice events, <code className="font-mono">lines.data[0].price.id</code>.
            </p>
            <p className="mt-2 text-warn-ink/80">
              Current price id: <code className="font-mono">{priceId || 'missing'}</code>
            </p>
          </div>}
        {error && <Button type="button" variant="outline" onClick={() => router.push('/')}>
            Back to pricing
          </Button>}
      </div>
    </div>;
}
export default function CheckoutPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><CheckoutContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}